import type { LucideIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { LayoutDashboard, FileCode, Pencil } from 'lucide-react';
import { useQueryState } from 'hooks/useQueryState';
import { cn } from 'helpers/cn';
import { DashboardTab } from './Dashboard';
import { RuntimeTab } from './Runtime';
import { EditorTab } from './Editor';
import { CreateFeatureFlagModal } from './CreateFeatureFlagModal';
import { useFeatureFlags, type FeatureFlag, type FeatureFlagScope } from '@/state/useFeatureFlags';

type Tab = 'dashboard' | 'runtime' | 'editor';

const TAB_TO_SCOPE: Record<Tab, FeatureFlagScope> = {
  dashboard: 'dashboard',
  runtime: 'runtime',
  editor: 'editor',
};

/**
 * Pills rather than the shadcn segmented `TabsList`: these re-scope the panel
 * under them, and the console draws that as a pill row. Icons are why this is not
 * `PillTabs` — that primitive takes a plain label.
 */
const TABS: { value: Tab; label: string; icon: LucideIcon }[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'runtime', label: 'Runtime', icon: FileCode },
  { value: 'editor', label: 'Editor', icon: Pencil },
];

export const FeatureFlags = () => {
  const [tab, setTab] = useQueryState<Tab>('tab', 'dashboard');
  const [isCreating, setIsCreating] = useState(false);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const { listFlags } = useFeatureFlags();

  const fetchFlags = async () => {
    const data = await listFlags();
    if (data) setFlags(data);
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  const handleCloseCreate = () => {
    setIsCreating(false);
    fetchFlags();
  };

  const filteredFlags = flags.filter((f) => f.scope === TAB_TO_SCOPE[tab]);

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="-mx-1 flex items-center gap-1.5 overflow-x-auto px-1">
        {TABS.map((entry) => {
          const Icon = entry.icon;
          return (
            <button
              key={entry.value}
              type="button"
              aria-pressed={tab === entry.value}
              onClick={() => setTab(entry.value)}
              className={cn(
                'micro-label rounded-pill border-line flex shrink-0 items-center gap-1.5 border px-3 py-1.5',
                'transition-colors duration-150',
                tab === entry.value
                  ? 'bg-sel-bg border-sel-line text-foreground'
                  : 'text-dim hover:bg-hover hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {entry.label}
            </button>
          );
        })}
      </div>

      {tab === 'dashboard' && <DashboardTab flags={filteredFlags} onCreateClick={() => setIsCreating(true)} />}
      {tab === 'runtime' && <RuntimeTab flags={filteredFlags} onCreateClick={() => setIsCreating(true)} />}
      {tab === 'editor' && <EditorTab flags={filteredFlags} onCreateClick={() => setIsCreating(true)} />}

      <CreateFeatureFlagModal open={isCreating} scope={TAB_TO_SCOPE[tab]} onClose={handleCloseCreate} />
    </div>
  );
};
